import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { ArrowLeft, CalendarDays, Check, Clock3 } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import "./CreateBooking.css";
import { CreateBookingStore } from "./CreateBookingStore";
import { Button } from "../../GloablComponents/Button";
import { AnalyticsClient } from "../../AnalyticsClient/AnalyticsClient";
import { LookstyLoader } from "../../GloablComponents/Loader/Loader";
import { DelayedLookstyLoader } from "../../GloablComponents/Loader/DelayedLookstyLoader";

export const CreateBookingPage = observer(() => {
  const store = useMemo(() => new CreateBookingStore(), []);
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const formattedDate = store.SelectedDate ? new Date(store.SelectedDate).toLocaleDateString("ru-RU") : "";

  useEffect(() => {
    const id = Number(params.id);
    store.setSource(searchParams.get("source"));
    if (id) store.load(id);
  }, [params.id, store]);

  useEffect(() => {
    const id = Number(params.id);

    if (!id) return;

    store.setSource(searchParams.get("source"));

    AnalyticsClient.track({
      eventType: "booking_started",
      source: searchParams.get("source") ?? "direct",
      entityType: "master",
      entityId: id,
    });
  }, [params.id, searchParams, store]);

  if (store.IsLoading) {
    return (
      <main className="booking-page booking-page-loading">
        <DelayedLookstyLoader variant="details" title="Загружаем запись" subtitle="Готовим услуги и свободное время" show={store.IsLoading} />
      </main>
    );
  }

  return (
    <>
      {store.SuccessMessage && <div className="booking-toast">{store.SuccessMessage}</div>}
      {store.IsCreating && (
        <div className="booking-loader-overlay">
          <LookstyLoader variant="details" title="Создаем запись" subtitle="Бронируем выбранное время" />
        </div>
      )}
      <main className="booking-page">
        <header className="booking-header">
          <button className="booking-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={22} />
          </button>

          <div>
            <span>Looksty</span>
            <h1>Запись</h1>
          </div>
        </header>

        {store.Error && <div className="booking-message error">{store.Error}</div>}

        <section className="booking-section">
          <h2>1. Выберите услугу</h2>

          <div className="booking-services">
            {store.Services.map((service) => (
              <button key={service.id} className={store.SelectedServiceId === service.id ? "booking-service-card active" : "booking-service-card"} onClick={() => store.selectService(service.id)}>
                <div>
                  <strong>{service.name}</strong>
                  <span>
                    {service.durationMinutes} мин · {service.price} ₽
                  </span>
                </div>

                {store.SelectedServiceId === service.id && <Check size={20} />}
              </button>
            ))}
          </div>

          {!store.Services.length && <div className="booking-empty">У мастера пока нет доступных услуг</div>}
        </section>

        {store.SelectedServiceId && (
          <section className="booking-section">
            <h2>2. Выберите день</h2>

            {store.IsLoadingDates && <div className="booking-message">Ищем свободные дни...</div>}

            {store.DatesError && <div className="booking-message error">{store.DatesError}</div>}

            {!store.IsLoadingDates && !store.DatesError && store.AvailableDates.length > 0 && (
              <>
                <div className="booking-date-strip">
                  <button type="button" className={store.IsCalendarOpen ? "booking-date-calendar active" : "booking-date-calendar"} onClick={() => store.toggleCalendar()}>
                    <CalendarDays size={18} />
                  </button>

                  <div className="booking-date-list">
                    {store.AvailableDateCards.map((day) => (
                      <button type="button" key={day.date} className={store.SelectedDate === day.date ? "booking-date-chip active" : "booking-date-chip"} onClick={() => store.selectDate(day.date)}>
                        <strong>
                          {day.dayNumber}
                          <span>{day.weekday}</span>
                        </strong>

                        <small>{day.isToday ? "Сегодня" : day.month}</small>
                      </button>
                    ))}

                    <button type="button" className="booking-date-more" onClick={() => store.toggleCalendar()}>
                      ещё
                    </button>
                  </div>
                </div>

                {store.IsCalendarOpen && (
                  <div className="booking-calendar">
                    <div className="booking-calendar-header">
                      <button type="button" className="booking-calendar-nav" disabled={!store.CanGoPrevMonth} onClick={() => store.goPrevMonth()}>
                        ←
                      </button>

                      <strong>{store.Calendar.title}</strong>

                      <button type="button" className="booking-calendar-nav" disabled={!store.CanGoNextMonth} onClick={() => store.goNextMonth()}>
                        →
                      </button>
                    </div>

                    <div className="booking-calendar-weekdays">
                      <span>Пн</span>
                      <span>Вт</span>
                      <span>Ср</span>
                      <span>Чт</span>
                      <span>Пт</span>
                      <span>Сб</span>
                      <span>Вс</span>
                    </div>

                    <div className="booking-calendar-grid">
                      {store.Calendar.days.map((day) => (
                        <button
                          key={day.date}
                          disabled={!day.isAvailableRange}
                          className={["booking-calendar-day", store.SelectedDate === day.date ? "active" : "", !day.isCurrentMonth ? "muted" : "", day.isToday ? "today" : ""].join(" ")}
                          onClick={() => store.selectDate(day.date)}
                        >
                          {day.isToday && <span>Сегодня</span>}
                          <strong>{day.dayNumber}</strong>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <p className="booking-calendar-hint">Показываем только дни, где у мастера есть свободное время.</p>
              </>
            )}

            {!store.IsLoadingDates && !store.DatesError && store.AvailableDates.length === 0 && <div className="booking-empty">У этой услуги пока нет свободных дней</div>}
          </section>
        )}

        {store.SelectedDate && (
          <section className="booking-section">
            <h2>3. Выберите время</h2>

            {store.IsLoadingSlots && <div className="booking-message">Ищем свободное время...</div>}

            {!store.IsLoadingSlots && (
              <div className="booking-slots">
                {store.Slots.map((slot) => (
                  <button key={slot.startsAt} className={store.SelectedSlot?.startsAt === slot.startsAt ? "booking-slot active" : "booking-slot"} onClick={() => store.selectSlot(slot)}>
                    <Clock3 size={16} />
                    {slot.time}
                  </button>
                ))}
              </div>
            )}

            {!store.IsLoadingSlots && store.Slots.length === 0 && <div className="booking-empty">На этот день нет свободного времени</div>}
          </section>
        )}

        {store.SelectedSlot && (
          <section className="booking-section">
            <h2>4. Телефон для связи</h2>

            <div className={store.IsPhoneReadonly ? "booking-phone-card readonly" : "booking-phone-card"}>
              <label>
                <span>Номер телефона</span>

                <input type="tel" inputMode="tel" placeholder="+7 999 123-45-67" value={store.PhoneDraft} readOnly={store.IsPhoneReadonly} onChange={(e) => store.setPhoneDraft(e.target.value)} />
              </label>

              <p>
                {store.IsPhoneReadonly
                  ? "Этот номер уже указан в вашем профиле. Он будет передан мастеру для связи."
                  : "Мастер сможет связаться с вами по этому номеру, если потребуется уточнить запись."}
              </p>

              {store.PhoneError && <div className="booking-phone-error">{store.PhoneError}</div>}
            </div>
          </section>
        )}

        {store.SelectedSlot && (
          <section className="booking-summary">
            <h2>Проверьте запись</h2>

            <p>
              {store.SelectedService?.name} · {formattedDate} · {store.SelectedSlot.time}
            </p>

            <Button
              disabled={store.IsCreating}
              onClick={() =>
                store.createBooking(() => {
                  setTimeout(() => {
                    navigate("/profile");
                  }, 1200);
                })
              }
            >
              {store.IsCreating ? "Создаем запись..." : "Записаться"}
            </Button>
          </section>
        )}
      </main>
    </>
  );
});
